import React, { useState } from 'react';
import styled from 'styled-components';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useTVLHistory, type TimeRange } from '../../hooks/useTVLHistory';

const ChartContainer = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const TimeRangeSelector = styled.div`
  display: flex;
  gap: 8px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const TimeRangeButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid ${props => props.active ? 'rgba(96, 165, 250, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  background: ${props => props.active ? 'rgba(96, 165, 250, 0.1)' : 'transparent'};
  color: ${props => props.active ? '#60a5fa' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Plus Jakarta Sans', sans-serif;
  
  &:hover {
    border-color: rgba(96, 165, 250, 0.5);
    color: #60a5fa;
  }
  
  @media (max-width: 768px) {
    flex: 1;
    padding: 10px 12px;
    font-size: 12px;
  }
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 400px;
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #ef4444;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
  
  p {
    margin: 8px 0 0 0;
    font-size: 14px;
  }
`;

/**
 * Custom tooltip for the chart
 */
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div style={{
        backgroundColor: 'rgba(20, 20, 25, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        padding: '12px',
        fontFamily: 'Plus Jakarta Sans, sans-serif'
      }}>
        <p style={{ margin: '0 0 8px 0', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
          {label}
        </p>
        <p style={{ margin: '0', color: '#60a5fa', fontSize: '14px', fontWeight: 600 }}>
          TVL: ${data.total_tvl.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          })}
        </p>
        <p style={{ margin: '4px 0 0 0', color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
          {data.pool_count} pool{data.pool_count !== 1 ? 's' : ''}
        </p>
      </div>
    );
  }
  return null;
};

/**
 * Format date for display
 */
const formatDate = (dateString: string, timeRange: TimeRange): string => {
  const date = new Date(dateString);
  
  if (timeRange === 'monthly') {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  
  // Daily view
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * TVL Chart Component
 * Displays historical TVL data as a bar chart
 */
export const TVLChart: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('daily');
  const days = timeRange === 'daily' ? 30 : 365; // Show 30 days for daily, 12 months for monthly
  
  const { snapshots, loading, error } = useTVLHistory(timeRange, days);

  // Transform data for Recharts
  const chartData = snapshots.map(snapshot => ({
    date: formatDate(snapshot.snapshot_date, timeRange),
    total_tvl: parseFloat(snapshot.total_tvl as any) || 0,
    pool_count: snapshot.pool_count,
    fullDate: snapshot.snapshot_date
  }));

  return (
    <ChartContainer>
      <ChartHeader>
        <ChartTitle>TVL History</ChartTitle>
        <TimeRangeSelector>
          <TimeRangeButton
            active={timeRange === 'daily'}
            onClick={() => setTimeRange('daily')}
          >
            Daily
          </TimeRangeButton>
          <TimeRangeButton
            active={timeRange === 'monthly'}
            onClick={() => setTimeRange('monthly')}
          >
            Monthly
          </TimeRangeButton>
        </TimeRangeSelector>
      </ChartHeader>

      {loading ? (
        <LoadingState>Loading chart data...</LoadingState>
      ) : error ? (
        <ErrorState>Error: {error}</ErrorState>
      ) : chartData.length === 0 ? (
        <EmptyState>
          <p>No TVL data available yet</p>
          <p style={{ fontSize: '12px', marginTop: '4px' }}>
            Data will appear after the first cron job run
          </p>
        </EmptyState>
      ) : (
        <ChartWrapper>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
              <XAxis
                dataKey="date"
                stroke="rgba(255, 255, 255, 0.5)"
                style={{ fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                stroke="rgba(255, 255, 255, 0.5)"
                style={{ fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="total_tvl"
                fill="#60a5fa"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>
      )}
    </ChartContainer>
  );
};

